import React, { useEffect } from "react";
import { Layout } from "views/Main/Layouts";
import { NavLink, Route, useQueryParamString } from "polyrouter";
import { useTranslation } from "utilities/i18n";
import {
  ProvisionedSettingsNav,
  ProvisionedDeviceSettingsRouter
} from "routes/Manage/Inventory/Settings/ProvisionedDeviceSettingsRouter";
import {
  useDeviceGroupQuery,
  useHardwareModelDetailsQuery,
  useSiteNameQuery,
  useGetUserGroupQuery,
  useGetDeviceNameQuery
} from "generated/graphql";
import { Loader, ProductImage } from "elements";
import {
  ProvisionedDesktopDeviceSettingsRouter,
  ProvisionedDesktopSettingsNav
} from "../Inventory/Settings-Desktop/ProvisionedDesktopDeviceSettingsRouter";
import { useTenantId } from "polyrouter/SelectedTenantProvider";
import {
  usePolicyFilterValue,
  defaultFilterState
} from "elements/Filter/PolicyFilter";

export type PolicyQueryParams =
  | "deviceModel"
  | "site"
  | "group"
  | "user_group"
  | "previous"
  | "device";

const PolicyDetailsView: React.FC = () => {
  const { t } = useTranslation();
  let [deviceModel] = useQueryParamString<PolicyQueryParams>("deviceModel");
  deviceModel = deviceModel ?? "";
  let [siteId] = useQueryParamString<PolicyQueryParams>("site");
  siteId = siteId ?? "";
  let [groupId] = useQueryParamString<PolicyQueryParams>("group");
  groupId = groupId ?? "";
  let [userGroupId] = useQueryParamString<PolicyQueryParams>("user_group");
  userGroupId = userGroupId ?? "";
  let [deviceId] = useQueryParamString<PolicyQueryParams>("device");
  deviceId = deviceId ?? "";
  const tenantId = useTenantId();
  const [filterState, setFilter] = usePolicyFilterValue();

  const {
    data: hardwareModelData,
    fetching: hardwareModelFetching
  } = useHardwareModelDetailsQuery({
    variables: {
      id: deviceModel
    }
  });

  useEffect(() => {
    const { site, user_group, group, device } = filterState;
    if (!siteId && !userGroupId && !groupId && (site || user_group || group)) {
      setFilter(defaultFilterState);
    }
    if (deviceId && !device) {
      setFilter({ device: deviceId });
    }
  }, [filterState, setFilter, siteId, userGroupId, groupId, deviceId]);

  const isDesktopDevice = hardwareModelData?.hardwareModel.supportsPolicies;
  const { data, fetching } = useSiteNameQuery({ variables: { id: siteId } });
  const siteName = data?.siteName?.name ?? "";

  let groupName = "";
  const { data: groupsData, fetching: groupsFetching } = useDeviceGroupQuery({
    variables: { id: groupId },
    pause: !groupId
  });
  if (groupId) {
    groupName = groupsData?.readGroup.groupDoc?.name ?? "";
  }

  const {
    data: userGroupData,
    fetching: userGroupFetching
  } = useGetUserGroupQuery({
    variables: {
      id: userGroupId,
      tenantId
    },
    pause: !userGroupId || !tenantId
  });
  const userGroupName = userGroupData?.getUserGroup.name ?? "";

  const id = filterState.device || deviceId;
  // this data is needed only for desktop devices
  const { data: deviceData, fetching: deviceFetching } = useGetDeviceNameQuery({
    variables: { id },
    pause: !isDesktopDevice || !id
  });
  const deviceName = deviceData?.device.name ?? "";
  const productId = deviceData?.device.productId ?? "";

  const { Sidebar, Content } = Layout;
  return (
    <Loader
      loading={
        fetching ||
        groupsFetching ||
        userGroupFetching ||
        hardwareModelFetching ||
        deviceFetching
      }
    >
      <Sidebar>
        <div className="text-center image-placeholder">
          <ProductImage
            hardwareModel={deviceModel}
            className="device-image full-width mt-25"
          />
        </div>
        <ul className="menu router">
          <li>
            <NavLink
              persist={[
                "PolicyFilterState",
                "previous",
                "deviceModel",
                "site",
                "user_group",
                "group",
                "device",
                "DeviceFilterState",
                "InsightsFilterState"
              ]}
              nest
              to="/settings"
            >
              {t("SETTINGS")}
            </NavLink>
            <Route nest path="/settings">
              {isDesktopDevice ? (
                <ProvisionedDesktopSettingsNav />
              ) : (
                <ProvisionedSettingsNav
                  id={deviceId}
                  model={deviceModel}
                  siteId={siteId}
                  groupId={groupId}
                />
              )}
            </Route>
          </li>
        </ul>
      </Sidebar>
      <Content scrolling className="device-details-content">
        <Route nest path="/settings">
          {isDesktopDevice ? (
            <ProvisionedDesktopDeviceSettingsRouter
              devicePolicyInfo={{
                id: deviceId,
                name: deviceName,
                productId: productId
              }}
              hardwareModel={hardwareModelData?.hardwareModel}
              site={{
                id: siteId,
                name: siteName
              }}
              group={{
                id: groupId,
                name: groupName
              }}
              userGroup={{
                id: userGroupId,
                name: userGroupName
              }}
            />
          ) : (
            <ProvisionedDeviceSettingsRouter
              id={id}
              model={deviceModel}
              siteId={siteId}
              siteName={siteName}
              groupId={groupId}
              groupName={groupName}
              isPolicyView={true}
            />
          )}
        </Route>
      </Content>
    </Loader>
  );
};

export default PolicyDetailsView;
